import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLatestBriefing, generateBriefing } from '../services/dailyBriefing';
import { formatRelativeDate } from '../utils/dateUtils';
import { renderDigestMarkdown } from '../utils/markdown';

const DailyBriefingWidget = () => {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();

  const fetchBriefing = async () => {
    try {
      setLoading(true);
      const data = await getLatestBriefing();
      setBriefing(data);
    } catch {
      setBriefing(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.stopPropagation();
    try {
      setGenerating(true);
      const data = await generateBriefing();
      setBriefing(data);
    } catch {
      // Silently fail — user can try from the full page
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    fetchBriefing();
  }, []);

  const previewThemes = (briefing?.themes || []).slice(0, 3);

  return (
    <div className="bg-surface rounded-lg shadow-md p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-y-1 mb-4">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold text-heading">
            ☀️ Daily Briefing
          </h2>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ml-3 text-secondary-text hover:text-body-text text-sm"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '▶' : '▼'}
          </button>
        </div>
        {briefing && (
          <span className="text-xs text-tertiary-text whitespace-nowrap">
            Updated {formatRelativeDate(briefing.generated_at || briefing.briefing_date)}
          </span>
        )}
      </div>

      {!isCollapsed && (
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-600 border-t-transparent"></div>
              <span className="ml-3 text-secondary-text text-sm">Loading briefing...</span>
            </div>
          ) : !briefing ? (
            <div className="text-center py-6">
              <div className="text-3xl mb-2">☀️</div>
              <p className="text-sm text-secondary-text mb-3">No daily briefing yet</p>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="text-teal-600 hover:text-teal-800 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {generating ? 'Generating...' : 'Generate briefing'}
              </button>
            </div>
          ) : (
            <div>
              {/* Summary preview */}
              {briefing.summary && (
                <p className="text-sm text-secondary-text mb-3 line-clamp-3">{briefing.summary}</p>
              )}

              {/* Theme previews */}
              <div className="space-y-3">
                {previewThemes.map((theme, idx) => (
                  <div
                    key={theme.name || idx}
                    className="border border-theme-border-subtle rounded-lg p-3 hover:border-teal-300 hover:shadow-sm cursor-pointer transition-all"
                    onClick={() => navigate('/daily')}
                  >
                    <h3 className="font-medium text-heading text-sm mb-1">{theme.name}</h3>
                    {theme.analysis && (
                      <p className="text-xs text-secondary-text mb-2 line-clamp-2">{theme.analysis}</p>
                    )}
                    <div className="space-y-1.5">
                      {theme.whats_new?.slice(0, 2).map((item, itemIdx) => (
                        <div key={itemIdx}>
                          <p className="text-xs text-body-text line-clamp-2">
                            {renderDigestMarkdown(typeof item === 'string' ? item : item.text)}
                          </p>
                        </div>
                      ))}
                      {(theme.whats_new?.length || 0) > 2 && (
                        <p className="text-xs text-tertiary-text">+{theme.whats_new.length - 2} more</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/daily')}
                  className="text-teal-600 hover:text-teal-800 text-sm font-medium transition-colors"
                >
                  View full briefing →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyBriefingWidget;
